import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useRouteMatch} from 'react-router-dom'

import {Box, Grid} from '@mui/material'
import type {Theme} from '@mui/material/styles'
import {makeStyles} from 'tss-react/mui'

import AppointmentList from '@progyny/pages/Consultations/Menopause/AppointmentList'
import MenopauseOverview from '@progyny/pages/Consultations/Menopause/MenopauseOverview'
import {useMenopauseConsultationAppointments} from '@progyny/pages/Consultations/Menopause/useMenopauseConsultationAppointments'
import P3Overview from '@progyny/pages/Consultations/P3/P3Overview'
import Navigation from '@progyny/pages/Consultations/Program/components/Navigation'
import CancelConsultation from '@progyny/pages/Consultations/components/CancelConsultation'
import OnboardingSection from '@progyny/pages/Consultations/components/OnboardingSection'
import {
  POSTPARTUM_HUB_PROGRAM_SLUGS,
  PREGNANCY_HUB_PROGRAM_SLUGS,
} from '@progyny/pages/Consultations/constants'
import {useP3ConsultationAppointments} from '@progyny/pages/Consultations/hooks/useP3ConsultationAppointments'

export type ActiveTab = 'overview' | 'upcoming' | 'past'

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '24px',
  },
}))
interface ProgramViewContentProps {
  consultation: any
  colors: any
  theme: any
  activeTab: ActiveTab
  onRefetchConsultationDetail?: (variables?: unknown) => Promise<unknown>
  consultationSlug: string
  onTabsVisibilityChange?: (visible: boolean) => void
}

const ProgramViewContent: React.FC<ProgramViewContentProps> = ({
  consultation,
  colors,
  theme,
  activeTab,
  onRefetchConsultationDetail,
  consultationSlug,
  onTabsVisibilityChange,
}) => {
  const {t} = useTranslation()
  const history = useHistory()
  const {path} = useRouteMatch()

  const menopauseAppointments = useMenopauseConsultationAppointments()

  const p3Appointments = useP3ConsultationAppointments(
    consultationSlug === 'postpartum'
      ? {
          onboardingConsultationSlug: 'postpartum',
          programSlugs: POSTPARTUM_HUB_PROGRAM_SLUGS,
        }
      : {
          onboardingConsultationSlug: 'pregnancy',
          programSlugs: PREGNANCY_HUB_PROGRAM_SLUGS,
        }
  )

  const {upcoming, past, hasUpcoming, hasPast, loading, refetchAppointments} =
    consultationSlug === 'menopause' ? menopauseAppointments : p3Appointments

  const refetchProgramDetail =
    consultationSlug === 'menopause'
      ? menopauseAppointments.refetchProgramDetail
      : undefined

  const showTabs = hasUpcoming || hasPast

  const [activeCancelUrl, setActiveCancelUrl] = React.useState<string | null>(
    null
  )

  React.useEffect(() => {
    // Hold off reporting false while still loading, so the page chrome
    // doesn't flash disableContentWrapper on and off before data arrives.
    onTabsVisibilityChange?.(loading || showTabs)
  }, [loading, showTabs, onTabsVisibilityChange])

  const navigationItems = [
    {
      itemID: 'overview',
      label: t('consultations_program_session_plan_consultation_overview'),
    },
    ...(hasUpcoming
      ? [
          {
            itemID: 'upcoming',
            label: t(
              'consultations_program_session_plan_upcoming_consultations'
            ),
          },
        ]
      : []),
    ...(hasPast
      ? [
          {
            itemID: 'past',
            label: t('consultations_program_session_plan_past_consultations'),
          },
        ]
      : []),
  ]

  const handleNavItemClick = (itemID: string) => {
    if (itemID === 'overview') {
      history.push(`/consultations/${consultationSlug}`)
    } else if (itemID === 'upcoming') {
      history.push(`/consultations/${consultationSlug}/upcoming`)
    } else if (itemID === 'past') {
      history.push(`/consultations/${consultationSlug}/past`)
    }
  }

  const activeNavItem = path.endsWith('/past')
    ? 'past'
    : path.endsWith('/upcoming')
      ? 'upcoming'
      : 'overview'

  const handleCancelBack = React.useCallback(() => {
    setActiveCancelUrl(null)
    refetchAppointments()
    refetchProgramDetail?.()
    if (onRefetchConsultationDetail) {
      onRefetchConsultationDetail()
    }
  }, [refetchAppointments, refetchProgramDetail, onRefetchConsultationDetail])

  React.useEffect(() => {
    if (activeTab !== 'overview' && activeCancelUrl) {
      setActiveCancelUrl(null)
    }
  }, [activeTab, activeCancelUrl])
  const {classes} = useStyles()

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <Grid item xs={12}>
              {activeCancelUrl ? (
                <CancelConsultation
                  cancelUrl={activeCancelUrl}
                  onBack={handleCancelBack}
                />
              ) : (
                <OnboardingSection
                  consultation={consultation}
                  colors={colors}
                  onCancelRequested={(url) => setActiveCancelUrl(url)}
                  alwaysShowBookingOption={
                    consultationSlug === 'menopause' ? true : undefined
                  }
                />
              )}
            </Grid>
            {!activeCancelUrl && (
              <Grid item xs={12}>
                <Box mt={theme.spacing(10)} mb={10} sx={{width: '100%'}}>
                  {consultationSlug === 'menopause' ? (
                    <MenopauseOverview colors={colors} />
                  ) : (
                    <P3Overview colors={colors} slug={consultationSlug} />
                  )}
                </Box>
              </Grid>
            )}
          </>
        )
      case 'upcoming':
        return (
          <AppointmentList
            items={upcoming}
            colors={colors}
            status={'upcoming'}
            loading={loading}
            consultationSlug={consultationSlug}
            onCancelBack={handleCancelBack}
          />
        )
      case 'past':
        return (
          <AppointmentList
            items={past}
            colors={colors}
            status={'completed'}
            loading={loading}
            consultationSlug={consultationSlug}
          />
        )
    }
  }

  return (
    <Box className={classes.container}>
      {showTabs && (
        <Navigation
          items={navigationItems}
          activeItemID={activeNavItem}
          onItemClick={handleNavItemClick}
          colors={colors}
        />
      )}
      {renderContent()}
    </Box>
  )
}

export default ProgramViewContent
